
import React, { useState } from 'react';

interface AdminHeaderProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
}

const AdminHeader: React.FC<AdminHeaderProps> = ({
  sidebarCollapsed,
  setSidebarCollapsed
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const notifications = [
    {
      id: 1,
      type: 'project',
      title: '项目进度更新',
      message: 'AI智能后台管理系统项目已完成80%',
      time: '2分钟前',
      unread: true,
      icon: 'ri-folder-line',
      color: 'text-blue-600'
    },
    {
      id: 2,
      type: 'client',
      title: '新客户咨询',
      message: '张先生咨询区块链开发项目',
      time: '15分钟前',
      unread: true,
      icon: 'ri-user-add-line',
      color: 'text-green-600'
    },
    {
      id: 3,
      type: 'system',
      title: '系统维护通知',
      message: '系统将于今晚23:00进行例行维护',
      time: '1小时前',
      unread: false,
      icon: 'ri-settings-line',
      color: 'text-orange-600'
    },
    {
      id: 4,
      type: 'payment',
      title: '收款提醒',
      message: '电商平台项目尾款已到账',
      time: '2小时前',
      unread: false,
      icon: 'ri-money-dollar-circle-line',
      color: 'text-green-600'
    },
    {
      id: 5,
      type: 'ai',
      title: 'AI报表生成完成',
      message: '本月项目数据分析报表已生成',
      time: '3小时前',
      unread: false,
      icon: 'ri-bar-chart-line',
      color: 'text-purple-600'
    }
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const markAsRead = (id: number) => {
    // 这里可以添加标记为已读的逻辑
    console.log('标记通知为已读:', id);
  };

  const markAllAsRead = () => {
    // 这里可以添加标记所有为已读的逻辑
    console.log('标记所有通知为已读');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-xl text-gray-600"></i>
          </button>
          
          <div className="ml-4">
            <h2 className="text-xl font-semibold text-gray-800">后台管理系统</h2>
            <p className="text-sm text-gray-500">欢迎回来，管理员</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button 
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-notification-line text-xl text-gray-600"></i>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">通知中心</h3>
                  {unreadCount > 0 && (
                    <button 
                      onClick={markAllAsRead}
                      className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer"
                    >
                      全部已读
                    </button>
                  )}
                </div>

                {/* Notifications List */}
                <div className="max-h-80 overflow-y-auto">
                  {notifications.map((notification) => (
                    <div 
                      key={notification.id}
                      onClick={() => markAsRead(notification.id)}
                      className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                        notification.unread ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          notification.unread ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <i className={`${notification.icon} ${notification.color} text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`text-sm font-medium ${
                              notification.unread ? 'text-gray-900' : 'text-gray-700'
                            }`}>
                              {notification.title}
                            </p>
                            {notification.unread && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-gray-200 text-center">
                  <button className="text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
                    查看所有通知
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <i className="ri-user-line text-white"></i>
              </div>
              <span className="text-gray-700 font-medium">管理员</span>
              <i className="ri-arrow-down-s-line text-gray-500"></i>
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <button className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                  <i className="ri-user-settings-line mr-2"></i>
                  个人设置
                </button>
                <button className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer">
                  <i className="ri-lock-line mr-2"></i>
                  修改密码
                </button>
                <hr className="my-2" />
                <button className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors cursor-pointer">
                  <i className="ri-logout-box-line mr-2"></i>
                  退出登录
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminHeader;
